import type { AppProps } from "next/app";
import Layout from "@/layout";
import { ConfigProvider } from "antd";
import { defaultTheme } from "@/theme/default";
import "./styles.scss";
import { useRouter } from "next/router";
import { appWithTranslation, useTranslation } from "next-i18next";
import nextI18nConfig from "../next-i18next.config";
import { useEffect, useLayoutEffect } from "react";
import { setLocaleCookie } from "@/i18n/utils/common";
import { canVisit } from "@/utils";
import { initSensors } from "@/sdk/sensors";
import { ANTD_LOCALE_MAP } from "@/i18n/antd-lang/antd-locale";
import { MOMENT_LOCALE_MAP } from "@/i18n/antd-lang/dayjs-lang/dayjs-locale";
import dayjs from "dayjs";
import "@/sentry/index";
import { myLocalFont, myLocalMarksFont } from "@/libs/font/fonts";
import { useSearchParams } from "next/navigation";
import classNames from "classnames";

// 排除的路径
const excludePaths = ["/demo", "/production"];
// 初始化sentry
// initSentry(commonData.sentryDns);

function App({ Component, pageProps }: AppProps) {
  const router = useRouter();
  const { pathname, push, locale = "zh" } = router;
  const { i18n } = useTranslation();
  const searchParams = useSearchParams();

  const localeLang = locale ?? "zh"; // 默认语言-中文
  useEffect(() => {
    // 当路由或搜索参数变化时滚动到顶部
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  }, [pathname, searchParams]);

  // 使用useLayoutEffect来确保在浏览器绘制前执行
  useLayoutEffect(() => {
    // 禁用浏览器的默认滚动恢复
    if ("scrollRestoration" in history) {
      history.scrollRestoration = "manual";
    }

    // 立即滚动到顶部
    window.scrollTo(0, 0);

    // 如果有性能问题，可以在下一帧再次确认
    requestAnimationFrame(() => {
      window.scrollTo(0, 0);
    });
  }, [router.asPath]); // 或 [pathname]

  // 切换路由自动滚到顶部 会导致语言切换自动回到顶部 如果想保留 则需要关闭此处代码 但是跳转路由也就保留位置了 除非用location 但是会有点慢
  // useEffect(() => {
  //   const handleRouteChange = () => {
  //     window.scrollTo(0, 0); // 滚动到顶部
  //   };

  //   router.events.on("routeChangeComplete", handleRouteChange);

  //   // 清理事件监听器
  //   return () => {
  //     router.events.off("routeChangeComplete", handleRouteChange);
  //   };
  // }, [router.events]);

  useEffect(() => {
    // 路由变化的时候设置cookie
    setLocaleCookie(localeLang);
    dayjs.locale(MOMENT_LOCALE_MAP[localeLang]);
  }, [locale]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      // 注册神策埋点
      if (window.sa === undefined) {
        initSensors().then(res => (window.sa = res));
      }
      // // 注册友盟监控  下线友盟
      // initApm(commonData.umengAppKey);
    }
  }, []);

  // useEffect(() => {
  //   // 定义设置根字体大小的函数
  //   const setRootFontSize = (): void => {
  //     const baseSize = 16; // 基础字体大小
  //     const designWidth = 1920; // 设计稿宽度
  //     const clientWidth = document.documentElement.clientWidth || window.innerWidth;
  //     const realWidth = Math.max(clientWidth, 1280);
  //     const fontSize = clientWidth <= 1280 ? baseSize : (realWidth / designWidth) * baseSize;
  //     document.documentElement.style.fontSize = `${fontSize}px`;
  //   };

  //   // 初始化调用一次
  //   setRootFontSize();

  //   // 添加事件监听器，当窗口大小改变时，更新根字体大小
  //   window.addEventListener("resize", setRootFontSize);

  //   // 清除事件监听器，防止内存泄漏
  //   return () => {
  //     window.removeEventListener("resize", setRootFontSize);
  //   };
  // }, []); // 依赖项为空数组，确保效果仅在组件挂载和卸载时运行

  // 不能访问的页面跳转到404
  if ((!canVisit(pathname, excludePaths) || pathname === "/404") && router.asPath !== `/404`) {
    // 如果是404页面，且路由不是404，则跳转到404页面
    if (typeof window !== "undefined") {
      push(`/404`);
    }
  }

  return (
    <ConfigProvider
      locale={ANTD_LOCALE_MAP[localeLang]}
      direction={i18n.dir()}
      theme={defaultTheme}
      autoInsertSpaceInButton={false}
    >
      <Layout {...pageProps}>
        <Component
          {...pageProps}
          className={classNames(myLocalFont.className, myLocalMarksFont.className)}
        />
      </Layout>
    </ConfigProvider>
  );
}
export default appWithTranslation(App, nextI18nConfig as any);
